import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { clearCasinoPopupQuery } from 'utils/casino/casino';
import { CUSTOM_CATEGORIES } from 'utils/constants/casino/custom-categories';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { GLOBALS } from 'utils/generic/global-variables';
import {
  getPageAliasFromUrl,
  isAreaRedirection,
  mountPoint
} from 'utils/generic/route-manipulation';
import { getSavedLocation } from 'components/QueryRoute';
import { useSearchParams } from 'hooks/useSearchParams';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { setHeaderState } from 'store/actions/app-data';
import { setCachedGamesInactive } from 'store/actions/new-casino';
import {
  getCanRequestCategoriesNestedData,
  getCanRequestProvidersNestedData
} from 'store/selectors/new-casino';

export const useCasinoSingleGameClose = (): {
  handleCloseClick: () => void;
} => {
  const dispatch = useDispatch();

  const { params } = useSbRouteMatch(
    `${mountPoint(window.getPathname(), false, true)}/:category`
  );

  const history = useHistory();
  const { gameId } = useSearchParams();
  const canRequestCategoriesNestedData = useSelector(
    getCanRequestCategoriesNestedData
  );

  const canRequestProvidersNestedData = useSelector(
    getCanRequestProvidersNestedData
  );

  const handleCloseClick = useCallback(() => {
    dispatch(setHeaderState({ isVisible: false, height: 0 }));
    let savedLocation = getSavedLocation('casinoSingleGame', false, true);
    const areaRedirection = isAreaRedirection(
      `${window.location.origin}/${savedLocation}`,
      true
    );

    const isCasinoPage =
      getPageAliasFromUrl(window.location.href) ===
      getPageAliasFromUrl(SpringConfigs.PAGE_URLS.casino);

    // in case when casino game opened from another page (Germany jurisdiction gambling areas)
    if (
      (!canRequestCategoriesNestedData &&
        !canRequestProvidersNestedData &&
        SpringConfigs.PAGE_URLS.casino &&
        isCasinoPage &&
        getPageAliasFromUrl(savedLocation) !==
          getPageAliasFromUrl(SpringConfigs.PAGE_URLS.casino)) ||
      areaRedirection.hasRedirect
    ) {
      savedLocation = '';
    }

    if (
      !(
        gameId &&
        !isCasinoPage &&
        !areaRedirection.hasRedirect &&
        getPageAliasFromUrl(savedLocation) !==
          getPageAliasFromUrl(SpringConfigs.PAGE_URLS.casino)
      )
    ) {
      getSavedLocation('casinoSingleGame', false, areaRedirection.hasRedirect);
    }

    dispatch(setCachedGamesInactive());

    const queryParams = clearCasinoPopupQuery();

    const locationPath = savedLocation.split('?');
    const pathname = window.getPathname();
    const [beforeCasinoMountPath] = pathname.split(
      `${SpringConfigs.CASINO_MOUNT_PATH}/`
    );

    if (
      !GLOBALS.casino.isCasinoCategoriesMounted &&
      beforeCasinoMountPath &&
      !isCasinoPage
    ) {
      locationPath[0] = beforeCasinoMountPath;
    }

    history.push({
      pathname: `${
        locationPath[0] ||
        `${mountPoint(pathname, false, true) as string}/${
          params.category || CUSTOM_CATEGORIES.all.id
        }`
      }`,
      state: null,
      search:
        !locationPath[1] && queryParams?.size
          ? queryParams.toString()
          : locationPath[1]
    });
  }, [gameId, params]);

  return { handleCloseClick };
};
